/* eslint-disable max-lines-per-function */
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Theme,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {Add, Delete, GetApp, InsertDriveFile} from '@material-ui/icons';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {Notification} from '../classes/Notification';
import locale from '../locale/Localization';
import {addContract, deleteContract} from '../script/person-service';
import {addNotifications} from '../store/appActions';
import {
  CONTRACT_REGISTRATION,
  CONTRACT_TERMINATION,
  SIGNATURE_DECLARATION
} from '../utils/constants';
import ConfirmationDialog from './ConfirmationDialog';
import NotaryContractDialog from './NotaryContractDialog';

const useStyles = makeStyles((theme: Theme) => ({
  title: {paddingTop: 0, paddingBottom: '1%', fontWeight: 550},
  subtitle: {paddingTop: 30, paddingBottom: '1%'},
  list: {backgroundColor: theme.palette.background.default},
  paper: {padding: theme.spacing(3, 2), width: 'inherit', marginTop: '0.1em'},
  nested: {
    paddingLeft: theme.spacing(4),
    paddingTop: 16,
    paddingBottom: 16
  },
  templates: {
    marginRight: '2em'
  },
  button: {marginBottom: 16}
}));

/**
 * Notary contract field.
 * @param {P} props the props.
 * @return {*} the rendered string.
 */
function NotaryContracts(props) {
  const classes = useStyles();
  const [openContractDialog, setOpenContractDialog] = React.useState(false);
  const [openDeleteContractDialog, setOpenDeleteContractDialog] =
    React.useState(false);
  const [signatureDeclaration, setSignatureDeclaration] = React.useState(
    props.signatureDeclaration
  );
  const [personId] = React.useState(props.personId);
  const [registration, setRegistration] = React.useState(props.registration);
  const [termination, setTermination] = React.useState(props.termination);
  const [contractToDelete, setContractToDelete] = React.useState(null);
  const [profileMode] = React.useState(props.profileMode);

  const addDocumentNotification = (notifications) =>
    props.dispatch(addNotifications(notifications));

  const handleFileSubmit = async (documentType: string, file: File) => {
    const response = await addContract(
      documentType,
      file,
      personId,
      profileMode
    );
    if (response.updated) {
      addDocumentNotification([
        new Notification(
          locale.uploadingContractSuccessNotification,
          'success',
          6000
        )
      ]);
      addContractItem(documentType);
      setOpenContractDialog(false);
    } else if (response.error && profileMode) {
      return response;
    } else {
      addDocumentNotification([
        new Notification(
          locale.uploadingContractErrorNotification,
          'error',
          6000
        )
      ]);
    }
  };

  const addContractItem = (documentType: string) => {
    if (profileMode && documentType === SIGNATURE_DECLARATION) {
      setSignatureDeclaration(true);
    } else if (documentType === CONTRACT_REGISTRATION) {
      setRegistration(true);
    } else if (documentType === CONTRACT_TERMINATION) {
      setTermination(true);
    }
  };

  const removeContractItem = (documentType: string) => {
    if (documentType === CONTRACT_REGISTRATION) {
      setRegistration(false);
    } else if (documentType === CONTRACT_TERMINATION) {
      setTermination(false);
    }
  };

  const removeContract = () => {
    deleteContract(contractToDelete, personId).then((response) => {
      if (response.updated) {
        addDocumentNotification([
          new Notification(
            locale.deleteContractSuccessNotification,
            'success',
            6000
          )
        ]);
        removeContractItem(contractToDelete);
        setOpenDeleteContractDialog(false);
        setContractToDelete(null);
      } else {
        addDocumentNotification([
          new Notification(
            locale.deleteContractErrorNotification,
            'error',
            6000
          )
        ]);
      }
    });
  };

  const getListItem = (label, type) => {
    return (
      <ListItem className={classes.nested}>
        <ListItemIcon>
          <InsertDriveFile />
        </ListItemIcon>
        <ListItemText primary={label} />
        <ListItemSecondaryAction>
          <IconButton
            edge='end'
            aria-label='function-edit'
            style={
              type === SIGNATURE_DECLARATION && props.editMode
                ? {marginRight: 36}
                : null
            }
            onClick={() =>
              window.open(
                '/notaries-public/' + personId + '/contracts/' + type,
                '_blank'
              )
            }
          >
            <GetApp />
          </IconButton>
          {props.editMode && type !== SIGNATURE_DECLARATION && (
            <IconButton
              color='secondary'
              variant='contained'
              component='span'
              aria-label='contract-delete'
              onClick={() => {
                setOpenDeleteContractDialog(true);
                setContractToDelete(type);
              }}
            >
              <Delete />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  return (
    <Paper className={classes.paper}>
      <Typography variant='subtitle1' gutterBottom className={classes.title}>
        {locale.notaryContracts}
      </Typography>
      {props.editMode && (
        <Button
          variant='contained'
          color='primary'
          size='small'
          className={classes.button}
          onClick={() => {
            setOpenContractDialog(true);
          }}
          startIcon={<Add />}
        >
          {props.profileMode
            ? locale.addSignatureDeclarationButton
            : locale.addButton}
        </Button>
      )}
      <Grid container item xs={12} spacing={3}>
        <Grid item xs={8}>
          <div className={classes.list}>
            {(signatureDeclaration || registration || termination) && (
              <List component='div' dense={true}>
                {signatureDeclaration &&
                  getListItem(
                    locale.signatureDeclarationDocument,
                    SIGNATURE_DECLARATION
                  )}
                {registration &&
                  getListItem(
                    locale.registrationDocument,
                    CONTRACT_REGISTRATION
                  )}
                {termination &&
                  getListItem(locale.terminationDocument, CONTRACT_TERMINATION)}
              </List>
            )}
          </div>
        </Grid>
        {profileMode && (
          <Grid item xs={12}>
            <Typography
              variant='subtitle2'
              gutterBottom
              className={classes.subtitle}
            >
              {locale.templateTitle}
            </Typography>
            <div>
              {locale.templatesDesciptionProfile}
              <div>
                <span className={classes.templates}>
                  {locale.signatureDeclarationDocument}
                  <IconButton
                    edge='end'
                    aria-label='function-edit'
                    onClick={() =>
                      window.open(
                        '/profile-update/signature-declaration',
                        '_blank'
                      )
                    }
                  >
                    <GetApp />
                  </IconButton>
                </span>
              </div>
            </div>
          </Grid>
        )}
      </Grid>
      <NotaryContractDialog
        open={openContractDialog}
        profileMode={profileMode}
        handleSubmit={(documentType, file) =>
          handleFileSubmit(documentType, file)
        }
        handleClose={() => setOpenContractDialog(false)}
      />
      <ConfirmationDialog
        open={openDeleteContractDialog}
        dialogText={locale.deleteContractDocumentDialogText}
        title={locale.deleteContractDocumentDialogTitle}
        handleClose={() => setOpenDeleteContractDialog(false)}
        handleSubmit={() => removeContract()}
        detailList={[]}
      />
    </Paper>
  );
}

/**
 * Map the properties.
 *
 * @param {*} dispatch
 * @return {Function} - The add user function.
 */
function mapDispatchToProps(dispatch) {
  return {
    dispatch: dispatch
  };
}

NotaryContracts.propTypes = {
  dispatch: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  personId: PropTypes.number.isRequired,
  signatureDeclaration: PropTypes.bool,
  registration: PropTypes.bool,
  termination: PropTypes.bool,
  profileMode: PropTypes.bool
};

export default connect(null, mapDispatchToProps)(NotaryContracts);
